import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, Shield, MapPin, Home, Lock, Eye, ArrowRight } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const EinbruchschutzPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';

  const faqData = [
    {
      question: 'Wie läuft ein Vor-Ort-Termin ab?',
      answer: 'Wir kommen zu Ihnen nach Hause, begutachten Türen, Fenster und andere potenzielle Schwachstellen. Anschließend besprechen wir mit Ihnen sinnvolle Sicherungsmaßnahmen und erstellen ein individuelles Konzept.'
    },
    {
      question: 'Mit welchen Kosten muss ich rechnen?',
      answer: 'Die Kosten hängen stark vom Umfang der Maßnahmen ab. Eine Beratung ist oft kostengünstig oder kostenlos. Zusatzsicherungen für Türen starten bei ca. 100-200€, hochwertige Sicherheitssysteme können mehr kosten. Wir beraten transparent.'
    },
    {
      question: 'Bringt nur ein neues Schloss etwas?',
      answer: 'Ein hochwertiger Sicherheitszylinder ist wichtig, aber nur ein Baustein. Wirklich effektiv wird Einbruchschutz durch die Kombination mehrerer Maßnahmen: guter Zylinder + Zusatzsicherungen + ggf. Fenstersicherungen.'
    },
    {
      question: 'Werden die Maßnahmen von der Versicherung anerkannt?',
      answer: 'Viele mechanische Sicherungen entsprechen den Anforderungen von Versicherungen. Wir beraten Sie, welche Maßnahmen sinnvoll sind und von Ihrer Versicherung empfohlen werden.'
    },
    {
      question: 'Gibt es Förderungen für Einbruchschutz?',
      answer: 'Ja! Die KfW-Bank bietet Zuschüsse für Einbruchschutzmaßnahmen. Auch manche Bundesländer und Kommunen haben eigene Förderprogramme. Wir informieren Sie gerne über aktuelle Möglichkeiten.'
    }
  ];

  const massnahmen = [
    'Verstärkung der Eingangstür (Panzerriegel, Querriegelschloss)',
    'Zusätzliche Türsicherungen (Bandseitensicherung, Schutzbeschläge)',
    'Fenstersicherungen (abschließbare Griffe, Pilzkopfverriegelungen)',
    'Hochwertige Sicherheitszylinder mit Aufbohr- und Ziehschutz',
    'Bewegungsmelder und Beleuchtung',
    'Sichtschutz und Grundstücksicherung'
  ];

  return (
    <>
      <Helmet>
        <title>Einbruchschutz in Dortmund – Sicherheitsberatung & Nachrüstung</title>
        <meta name="description" content="Professioneller Einbruchschutz in Dortmund: Beratung, Sicherheitsanalyse vor Ort & Nachrüstung von Türen und Fenstern. Jetzt Termin vereinbaren: 0231 39085116." />
        <link rel="canonical" href="https://schluesseldienst.services/leistungen/einbruchschutz/" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[
          { label: 'Leistungen', path: '/leistungen' },
          { label: 'Einbruchschutz' }
        ]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-gray-900">
          Einbruchschutz & Sicherheitsberatung in Dortmund
        </h1>

        <div className="max-w-4xl mb-12">
          <p className="text-lg text-gray-700 mb-4">
            <strong>Schützen Sie Ihr Zuhause vor Einbrechern!</strong> Jeden Tag ereignen sich in Deutschland 
            zahlreiche Einbrüche – doch mit den richtigen Sicherheitsmaßnahmen können Sie das Risiko deutlich 
            senken. Viele Einbrecher geben nach wenigen Minuten auf, wenn sie auf mechanische Sicherungen stoßen.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Als erfahrener Schlüsseldienst in Dortmund bieten wir Ihnen professionelle Sicherheitsberatung 
            und die fachgerechte Nachrüstung von mechanischen Sicherungssystemen. Wir analysieren die 
            Schwachstellen Ihrer Türen und Fenster und empfehlen Ihnen passende Lösungen – ganz individuell 
            auf Ihre Situation zugeschnitten.
          </p>
          <p className="text-lg text-gray-700 mb-6">
            Die Schössow Dienstleistungen UG steht für transparente Beratung, hochwertige Produkte und 
            fachgerechten Einbau. Investieren Sie in Ihre Sicherheit – wir helfen Ihnen dabei.
          </p>
          
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Sicherheitsberatung gewünscht?</h3>
                <p className="text-gray-700">Vereinbaren Sie jetzt einen Termin – wir beraten Sie vor Ort!</p>
              </div>
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-lg transition-all shadow-lg whitespace-nowrap"
              >
                <Phone size={20} />
                {displayPhone}
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Vorteile auf einen Blick</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Individuelle Sicherheitsanalyse</strong> vor Ort bei Ihnen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Mechanische Sicherungen</strong> für Türen & Fenster</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Kombination mit hochwertigen Zylindern</strong> möglich</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Transparente Beratung</strong> ohne Verkaufsdruck</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Fachgerechter Einbau</strong> aller Sicherheitssysteme</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>KfW-Förderung</strong> möglich – wir informieren Sie</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Ablauf der Sicherheitsberatung</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
                Terminvereinbarung
              </h3>
              <p className="text-gray-700">
                Sie rufen uns unter <strong>{displayPhone}</strong> an und vereinbaren einen Termin 
                für eine Sicherheitsanalyse vor Ort. Wir sind flexibel und kommen zu einem für Sie 
                passenden Zeitpunkt.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                Aufnahme der Schwachstellen
              </h3>
              <p className="text-gray-700">
                Unser Techniker begutachtet Ihre Türen, Fenster, Kellerzugänge und andere potenzielle 
                Eintrittspunkte. Wir identifizieren Schwachstellen und erklären Ihnen, wo Einbrecher 
                typischerweise ansetzen.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                Empfehlungskonzept
              </h3>
              <p className="text-gray-700">
                Basierend auf der Analyse erstellen wir ein individuelles Sicherheitskonzept. Wir empfehlen 
                Ihnen passende Maßnahmen – vom einfachen Zusatzschloss bis zur umfassenden Nachrüstung – 
                und besprechen die Kosten transparent mit Ihnen.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                Umsetzung
              </h3>
              <p className="text-gray-700">
                Wenn Sie sich für Maßnahmen entscheiden, führen wir diese fachgerecht aus. Je nach Umfang 
                kann dies sofort erfolgen oder wir vereinbaren einen weiteren Termin für die Installation.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Mögliche Maßnahmen</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <p className="text-gray-700 mb-6">
              <strong>Mechanischer Einbruchschutz ist nachweislich wirksam.</strong> Studien zeigen, dass 
              viele Einbrecher nach 2-3 Minuten aufgeben, wenn sie auf Widerstand stoßen. Diese Maßnahmen 
              können wir für Sie umsetzen:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {massnahmen.map((massnahme, index) => (
                <div key={index} className="flex gap-3">
                  <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{massnahme}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Einbruchschutz in Kombination mit Schlosswechsel</h2>
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
            <div className="flex gap-3">
              <Lock className="w-6 h-6 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">Perfekt kombinierbar</h3>
                <p className="text-gray-700 mb-3">
                  Ein hochwertiger Sicherheitszylinder ist die Basis für guten Einbruchschutz. Wenn Sie 
                  ohnehin einen Zylindertausch planen, ist dies der ideale Zeitpunkt, um auch weitere 
                  Sicherungen anzubringen.
                </p>
                <Link 
                  to="/leistungen/schlosswechsel"
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-semibold transition-all"
                >
                  Mehr zum Schlosswechsel
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Preise & Förderung</h2>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border-2 border-orange-200">
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-bold mb-3">Individuelle Preisgestaltung</h3>
                <p className="text-gray-700 mb-4">
                  Die Kosten für Einbruchschutzmaßnahmen hängen stark vom Umfang ab. Eine Beratung ist 
                  oft kostengünstig oder bei späterer Beauftragung kostenlos.
                </p>
                <p className="text-gray-700">
                  <strong>Beispiele:</strong> Zusatzsicherungen für Türen ab ca. 100-200€, 
                  Fenstersicherungen ab ca. 50-100€ pro Fenster. Wir erstellen Ihnen ein transparentes Angebot.
                </p>
              </div>
              
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Home className="w-5 h-5 text-green-600" />
                  KfW-Förderung möglich
                </h3>
                <p className="text-gray-700">
                  Die KfW-Bank fördert Einbruchschutzmaßnahmen mit Zuschüssen. Auch einige Bundesländer 
                  und Kommunen bieten eigene Förderprogramme. Wir informieren Sie gerne über aktuelle 
                  Möglichkeiten und unterstützen Sie bei der Antragstellung.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Einsatzgebiet in Dortmund</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex gap-3 mb-4">
              <MapPin className="w-6 h-6 text-orange-600 flex-shrink-0" />
              <p className="text-gray-700">
                <strong>Sicherheitsberatung in ganz Dortmund.</strong> Wir kommen zu Ihnen nach Hause 
                und nehmen uns Zeit für eine gründliche Analyse.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Jetzt Sicherheit erhöhen – Termin vereinbaren!</h2>
          <p className="text-lg mb-6 text-gray-300">
            Individuelle Beratung • Mechanische Sicherungen • KfW-Förderung<br />
            Schützen Sie Ihr Zuhause vor Einbrechern
          </p>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl"
          >
            <Phone size={24} />
            {displayPhone}
          </a>
        </div>

        <div className="mt-12 max-w-4xl">
          <h3 className="text-xl font-bold mb-4 text-gray-900">Weitere Leistungen</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/leistungen" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Alle Leistungen <ArrowRight size={18} />
            </Link>
            <Link to="/leistungen/schlosswechsel" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Schlosswechsel <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default EinbruchschutzPage;
